import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The animals are the light.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'jellyfish',
  family: 'organic',
  danceability: 0.22,
  energyLevel: 0.20,
  bpmMin: 40,
  bpmMax: 120,
  movement: 0.30,
  tonality: 0.92,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.40] },
  enabled: true
};

// Jellyfish: bioluminescent jellies drifting in the dark, each one
// contracting its bell to move and sending a pulse of light down its
// tentacles.
//
// THE CONTRACTION PROPELS. A jelly does not glide: the bell snaps shut in
// 0.4 s and the animal jumps forward, then relaxes over 2.5 s and drifts to
// a near stop. The velocity here is an impulse per contraction plus drag,
// never a constant - a jelly moving at one speed is a balloon. The rim of
// the bell closes AFTER the apex (the contraction the rim sees is the
// apex's, delayed), like an umbrella, and the light leaves the contraction
// and runs down the tentacles with a growing delay.
//
// THE TIMING IS THERE AND NEVER SHOWS. Every jelly contracts once every
// four bars at a phase of its own; with twelve of them one contracts about
// every beat, and no two agree. Only on the major phrases do three go
// together - the one moment the tempo surfaces.
//
// THE TENTACLES TRAIL. Each is a chain of sixteen nodes that follow the one
// above with a lag that grows towards the tip (Cascade's delay, as a
// follow-the-leader chain): the top goes with the body, the bottom drags.

// --- the animal --------------------------------------------------------------
const CONTRACT_S = 0.4;              // the snap
const RELAX_S = 2.5;                 // the recovery
const RIM_DELAY_S = 0.16;            // the rim closes this much after the apex
const CONTRACT_DEPTH = 0.38;         // how far the bell's radius drops
const IMPULSE = 2.0;                 // world units per second, per contraction (x the scale)
const DRAG = 1.35;                   // per second
const BELL_SEGMENTS = [24, 16];
const CANALS = 8;
const TENTACLE_NODES = 16;
const ARMS = 4;
const TENTACLE_LEN = 2.8;            // per bell radius: long, trailing
const ARM_LEN = 1.7;
const PULSE_TRAVEL_S = 1.5;          // the light reaches the tip in
const CONTRACT_EVERY_BARS = 4;
const RISE = 0.10;                   // a slow upward bias, world units per second
const BELL_R = 1.0;
const BIOLUM = [0.30, 0.85, 0.95];   // the blue-green, never the palette

// --- the water -----------------------------------------------------------------
const Z_NEAR = -7, Z_FAR = -34;
const VIEW_ATTEN = 0.045;            // Kelp's second pass: paler and bluer with distance
const PARTICLES = 400;
const SAFE_FLOOR = 0.5;

const TIER_LAYOUT = {
  0: { jellies: 5, tentacles: 10, particles: false },
  1: { jellies: 8, tentacles: 16, particles: true },
  2: { jellies: 12, tentacles: 24, particles: true }
};
const MAX_JELLIES = 12;

const _size = new THREE.Vector2();
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _m = new THREE.Matrix4();
const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
const UP = new THREE.Vector3(0, 1, 0);

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function lcg(seed) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}
// A smooth field for the drift, and its curl: divergence-free by
// construction, so the animals wander without piling up anywhere.
function field(x, y, t) {
  return Math.sin(x * 0.37 + t * 0.11) * Math.cos(y * 0.29 - t * 0.07) + 0.6 * Math.sin(x * 0.11 - y * 0.23 + t * 0.05) + 0.4 * Math.cos((x + y) * 0.53 + t * 0.13);
}
function curl(x, y, t, out) {
  const e = 0.05;
  out.x = (field(x, y + e, t) - field(x, y - e, t)) / (2 * e);
  out.y = -(field(x + e, y, t) - field(x - e, y, t)) / (2 * e);
}

// --- shaders -------------------------------------------------------------------

const SAFE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAt(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// Kelp's view attenuation, on black water: with distance a jelly loses its
// green first and goes paler and bluer, then into the water.
const WATER_CHUNK = `
  uniform vec3 uWater;
  vec3 waterView(vec3 col, float dist) {
    vec3 keep = exp(-dist * ${VIEW_ATTEN.toFixed(3)} * vec3(0.9, 0.55, 0.30));
    return col * keep + uWater * (1.0 - keep.b) * 0.6;
  }
`;

const waterVertexShader = `
  precision mediump float;
  varying vec2 vNdc;
  void main() { vNdc = position.xy; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const waterFragmentShader = `
  precision mediump float;
  uniform vec3 uWater;
  varying vec2 vNdc;
  void main() {
    // Deep water: the palette's hue at almost no light, darker below.
    float up = vNdc.y * 0.5 + 0.5;
    vec3 col = uWater * (0.5 + 0.5 * up) + vec3(0.001, 0.002, 0.006);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// The bell: a hemisphere whose horizontal radius follows the contraction,
// the rim lagging the apex; a fine undulation at the rim; radial canals
// and a rim band that light up on the snap. Translucent, the inside face
// brighter, the central organ seen through the apex.
const bellVertexShader = `
  precision mediump float;
  uniform float uC;            // the apex's contraction, 0..1
  uniform float uCRim;         // the same, delayed: the rim's
  uniform float uWave;         // rim undulation amplitude
  uniform float uTime;
  uniform float uSpin;
  varying float vRim;          // 0 apex .. 1 rim
  varying float vAz;           // 0..1 round the bell
  varying vec3 vWorld;
  varying float vSafe;
  varying float vDist;
  ${SAFE_CHUNK}
  void main() {
    vec3 p = position;
    float rim = clamp(acos(clamp(p.y, 0.0, 1.0)) / 1.5708, 0.0, 1.0);
    float az = atan(p.z, p.x) / 6.2832 + 0.5;
    float c = mix(uC, uCRim, smoothstep(0.0, 1.0, rim));
    // The snap: the radius drops, the bell lengthens; the rim curls in a
    // little more than the sides.
    float s = 1.0 - ${CONTRACT_DEPTH.toFixed(2)} * c * (0.8 + 0.5 * rim * rim);
    float v = 1.0 + 0.30 * c;
    float wave = uWave * 0.07 * sin(az * 6.2832 * 9.0 + uTime * 2.6 + uSpin * 3.0) * smoothstep(0.55, 1.0, rim);
    p.xz *= s + wave;
    p.y = p.y * v - 0.10 * c * rim * rim;
    vRim = rim; vAz = az;
    vec4 world = modelMatrix * vec4(p, 1.0);
    vWorld = world.xyz;
    vec4 mv = viewMatrix * world;
    vDist = -mv.z;
    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    vSafe = safeAt(clip);
  }
`;
const bellFragmentShader = `
  precision mediump float;
  uniform float uC;
  uniform float uPulse;        // the light at the rim, just after a snap
  uniform float uGain;
  varying float vRim;
  varying float vAz;
  varying vec3 vWorld;
  varying float vSafe;
  varying float vDist;
  ${WATER_CHUNK}
  void main() {
    vec3 base = vec3(${BIOLUM[0]}, ${BIOLUM[1]}, ${BIOLUM[2]});
    float inner = gl_FrontFacing ? 0.0 : 1.0;
    // The membrane: dim, a shade brighter towards the rim; the inside face
    // brighter, and the organ glowing through the apex.
    float membrane = 0.32 + 0.25 * vRim;
    float organ = exp(-vRim * vRim * 7.0) * (0.55 + 0.9 * uC);
    // The canals: eight lines from the apex, lit by the contraction.
    float canal = 1.0 - smoothstep(0.0, 0.045, abs(fract(vAz * ${CANALS.toFixed(1)}) - 0.5) * 2.0 / ${CANALS.toFixed(1)} * ${CANALS.toFixed(1)});
    canal *= (0.15 + 1.1 * uC) * (0.3 + 0.7 * (1.0 - vRim));
    // The rim: a brighter band that carries the pulse.
    float band = smoothstep(0.88, 0.97, vRim) * (0.5 + 1.4 * uPulse);
    vec3 col = base * (membrane + organ * (0.6 + 0.6 * inner) + canal + band) * (1.0 + 0.35 * inner) * uGain;
    col = waterView(col, vDist);
    float alpha = (0.35 + 0.15 * inner + 0.25 * band + 0.2 * canal + 0.3 * organ) * vSafe * uGain;
    gl_FragColor = vec4(col, clamp(alpha, 0.0, 0.85));
  }
`;

// Suspended particles, lit by the nearest jellies.
const particleVertexShader = `
  precision mediump float;
  attribute float aSeed;
  uniform float uTime;
  uniform float uPixelScale;
  uniform vec4 uJelly[${MAX_JELLIES}];   // xyz, glow
  uniform float uCount;
  varying float vLight;
  ${SAFE_CHUNK}
  void main() {
    vec3 p = position;
    p.x += sin(uTime * 0.13 + aSeed * 6.2832) * 0.6;
    p.y += uTime * 0.05 * (0.5 + aSeed) ;
    p.y = mod(p.y + 14.0, 28.0) - 14.0;
    p.z += cos(uTime * 0.09 + aSeed * 4.0) * 0.5;
    float light = 0.0;
    for (int i = 0; i < ${MAX_JELLIES}; i++) {
      if (float(i) >= uCount) break;
      vec3 d = p - uJelly[i].xyz;
      light += uJelly[i].w / (1.0 + dot(d, d) * 0.12);
    }
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    gl_PointSize = clamp((0.05 + 0.05 * aSeed) * uPixelScale / max(-mv.z, 0.001), 1.0, 4.0);
    vLight = min(light, 1.5) * safeAt(clip);
  }
`;
const particleFragmentShader = `
  precision mediump float;
  varying float vLight;
  void main() {
    if (vLight < 0.01) discard;
    vec2 d = gl_PointCoord - 0.5;
    float a = 1.0 - smoothstep(0.1, 0.5, length(d));
    gl_FragColor = vec4(vec3(0.55, 0.85, 0.95) * a * vLight * 0.35, 1.0);
  }
`;

export class JellyfishVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER_LAYOUT[this._tier] || TIER_LAYOUT[1];
    this._debugPulse = urlParam('jellypulse') === '1';
    this._single = urlParam('jellysingle') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._placeCamera();
    this._time = 0;
    this._beatSeconds = 0.5;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._freeBeatAt = 0; this._beatCount = 0; this._barCount = 0;
    this._bars = 0;                        // a continuous bar count
    this._debugPulseAt = 1.5;
    this._water = new THREE.Color(0.01, 0.02, 0.05);
    this._objects = []; this._materials = []; this._geometries = [];
    this._rnd = lcg(0x3e11);
    this._shared = {
      uWater: { value: this._water },
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._buildWater();
    this._buildJellies();
    this._buildTentacles();
    if (this._layout.particles && !this._single) this._buildParticles();
    this._applyPalette(null);
  }

  _placeCamera() {
    const c = this._camera;
    c.position.set(0, 0, 0);
    c.lookAt(0, 0, -20);
    c.updateMatrixWorld();
  }

  _add(mesh, geo, mat, order) {
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    this._objects.push(mesh);
    if (geo) this._geometries.push(geo);
    if (mat) this._materials.push(mat);
    this._scene.add(mesh);
    return mesh;
  }

  _uniforms(extra) {
    const u = {};
    for (const k of Object.keys(this._shared)) u[k] = this._shared[k];
    return Object.assign(u, extra || {});
  }

  // The visible half-width at a depth, for placing and wrapping.
  _halfW(z) { return -z * Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2) * (this._camera.aspect || 16 / 9); }
  _halfH(z) { return -z * Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2); }

  _buildWater() {
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({ uniforms: this._uniforms(), vertexShader: waterVertexShader, fragmentShader: waterFragmentShader, depthTest: false, depthWrite: false, transparent: true });
    const mesh = this._add(new THREE.Mesh(geo, mat), geo, mat, -1000);
    const self = this;
    mesh.onBeforeRender = function (renderer) { self._onFrame(renderer); };
  }

  // Is a projected point inside the cover's zone (plus a margin)?
  // R19: the zone is the stage block (screen centre); the cover only says
  // whether there is one at all.
  _inSafeZone(x, y, z) {
    const sh = safeZoneUniforms.uSafeHalfSize.value;
    if (sh.x <= 0 || sh.y <= 0) return false;
    _v.set(x, y, z).project(this._camera);
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value, a = safeZoneUniforms.uAspect.value;
    const sx = _v.x * a, sy = _v.y;
    return Math.abs(sx - c.x) < hs.x + 0.25 && Math.abs(sy - c.y) < hs.y + 0.25;
  }

  _buildJellies() {
    this._jellies = [];
    const n = this._single ? 1 : MAX_JELLIES;
    const geo = new THREE.SphereGeometry(BELL_R, BELL_SEGMENTS[0], BELL_SEGMENTS[1], 0, Math.PI * 2, 0, Math.PI / 2);
    this._geometries.push(geo);
    for (let i = 0; i < n; i++) {
      const u = this._uniforms({ uC: { value: 0 }, uCRim: { value: 0 }, uWave: { value: 0.5 }, uTime: { value: 0 }, uSpin: { value: 0 }, uPulse: { value: 0 }, uGain: { value: 1 } });
      const mat = new THREE.ShaderMaterial({ uniforms: u, vertexShader: bellVertexShader, fragmentShader: bellFragmentShader, transparent: true, blending: THREE.NormalBlending, side: THREE.DoubleSide, depthTest: true, depthWrite: false });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.matrixAutoUpdate = false;
      this._add(mesh, null, mat, -700 + i);
      const j = {
        mesh, uniforms: u,
        pos: new THREE.Vector3(), vel: new THREE.Vector3(), heading: new THREE.Vector3(0, 1, 0),
        phase: this._rnd(), lastSnap: -1e9, spin: this._rnd() * Math.PI * 2, spinRate: (this._rnd() - 0.5) * 0.3,
        scale: this._single ? 2.4 : 1.4 + this._rnd() * 1.1,
        c: 0, cRim: 0, gain: 0, active: false, seed: this._rnd(),
        tentacles: []
      };
      this._jellies.push(j);
      this._spawn(j, true);
      // The first six are on screen from the start; the rest come up from
      // below as the track goes on.
      j.active = i < Math.min(6, this._single ? 1 : this._layout.jellies);
      // An inactive bell is HIDDEN, not merely faded: its matrix is never
      // written, so it would sit at the origin, around the camera, and its
      // membrane would be the whole frame.
      mesh.visible = j.active;
    }
  }

  // A jelly enters from below the frame (or anywhere, at the start), never
  // inside the cover's zone.
  _spawn(j, initial) {
    const z = this._single ? -9 : Z_NEAR - this._rnd() * (Z_NEAR - Z_FAR);
    const hw = this._halfW(z), hh = this._halfH(z);
    let x = 0, y = 0;
    for (let tries = 0; tries < 20; tries++) {
      x = (this._rnd() * 2 - 1) * hw * 0.9;
      y = initial ? (this._rnd() * 2 - 1) * hh * 0.9 : -hh - 2 - this._rnd() * 3;
      if (this._single) { x = 0; y = -0.6; }
      if (!this._inSafeZone(x, y, z)) break;
    }
    j.pos.set(x, y, z);
    j.vel.set(0, 0, 0);
    j.heading.set((this._rnd() - 0.5) * 0.5, 1, (this._rnd() - 0.5) * 0.2).normalize();
    j.c = 0; j.cRim = 0; j.lastSnap = -1e9;
    j.gain = initial ? 1 : 0;
    // The tentacles start hanging straight down from the rim.
    for (const t of j.tentacles) this._restTentacle(j, t);
  }

  _buildTentacles() {
    const L = this._layout;
    const K = this._single ? 24 : L.tentacles;
    let fine = 0, arms = 0;
    for (const j of this._jellies) {
      for (let k = 0; k < K; k++) { j.tentacles.push(this._makeTentacle(j, k / K, false)); fine++; }
      for (let k = 0; k < ARMS; k++) { j.tentacles.push(this._makeTentacle(j, (k + 0.5) / ARMS, true)); arms++; }
    }
    this._fine = this._lineSet(fine * TENTACLE_NODES, 1.6, -800);
    this._arms = this._lineSet(arms * TENTACLE_NODES, 3.6, -790);
  }

  _makeTentacle(j, az, arm) {
    const nodes = [];
    for (let i = 0; i <= TENTACLE_NODES; i++) nodes.push(new THREE.Vector3());
    const t = { az, arm, nodes, len: (arm ? ARM_LEN : TENTACLE_LEN) * (0.85 + this._rnd() * 0.3), r: arm ? 0.35 : 0.97, seed: this._rnd() };
    this._restTentacle(j, t);
    return t;
  }

  _restTentacle(j, t) {
    const L = t.len * j.scale * BELL_R;
    const ang = t.az * Math.PI * 2;
    const x = j.pos.x + Math.cos(ang) * t.r * j.scale * BELL_R, z = j.pos.z + Math.sin(ang) * t.r * j.scale * BELL_R;
    for (let i = 0; i <= TENTACLE_NODES; i++) t.nodes[i].set(x, j.pos.y - (i / TENTACLE_NODES) * L, z);
  }

  _lineSet(segments, widthPx, order) {
    const geo = new LineSegmentsGeometry();
    const pos = new Float32Array(segments * 6), col = new Float32Array(segments * 6);
    geo.setPositions(pos); geo.setColors(col);
    geo.instanceCount = 0;
    const mat = new LineMaterial({
      linewidth: widthPx, vertexColors: true, transparent: true, opacity: 1.0,
      blending: THREE.AdditiveBlending, depthTest: true, depthWrite: false,
      dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false
    });
    mat.resolution.set(1280, 720);
    const mesh = new LineSegments2(geo, mat);
    this._add(mesh, geo, mat, order);
    return { mesh, geo, pos, col, posBuf: geo.attributes.instanceStart.data, colBuf: geo.attributes.instanceColorStart.data, mat };
  }

  _buildParticles() {
    const n = PARTICLES;
    const pos = new Float32Array(n * 3), seed = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const z = Z_NEAR - this._rnd() * (Z_NEAR - Z_FAR);
      pos[i * 3] = (this._rnd() * 2 - 1) * this._halfW(z) * 1.1;
      pos[i * 3 + 1] = (this._rnd() * 2 - 1) * 14;
      pos[i * 3 + 2] = z;
      seed[i] = this._rnd();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    const jelly = [];
    for (let i = 0; i < MAX_JELLIES; i++) jelly.push(new THREE.Vector4(0, 0, 0, 0));
    this._particleUniforms = this._uniforms({ uTime: { value: 0 }, uPixelScale: { value: 540 }, uJelly: { value: jelly }, uCount: { value: 0 } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._particleUniforms, vertexShader: particleVertexShader, fragmentShader: particleFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthTest: true, depthWrite: false });
    this._add(new THREE.Points(geo, mat), geo, mat, -900);
  }

  _onFrame(renderer) {
    renderer.getDrawingBufferSize(_size);
    this._fine.mat.resolution.set(_size.x, _size.y);
    this._arms.mat.resolution.set(_size.x, _size.y);
    if (this._particleUniforms) this._particleUniforms.uPixelScale.value = (_size.y / 2) / Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2);
  }

  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const src = vivid.length ? vivid[0] : (dominant.length ? dominant[0] : null);
    if (src) _col.set(src); else _col.setRGB(0.2, 0.4, 0.9);
    _col.getHSL(_hsl);
    // The palette tints the WATER only: its hue, deep and dark.
    this._water.setHSL(_hsl.h, 0.55, 0.012);
  }

  onTrackChange(profile) { this._applyPalette(profile); }

  _snap(j, t) { j.lastSnap = t; }

  update(deltaTime, profile) {
    this._placeCamera();
    const director = profile.director || {};
    const paused = profile.playbackState !== 'PLAYING';
    if (profile.artworkPalette && profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile); }
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 180) : 0;
    this._beatSeconds = bpm ? 60 / bpm : 0.75;
    const beat = this._beatSeconds;
    const measured = !!director.hasReliableGrid;
    const bands = director.bands || { low: 0, mid: 0, high: 0 };
    const low = measured ? (bands.low || 0) : 0.5, mid = measured ? (bands.mid || 0) : 0.5, high = measured ? (bands.high || 0) : 0.5;
    const progress = profile.progress || 0;
    const L = this._layout;
    const wanted = this._single ? 1 : Math.min(L.jellies, Math.round(6 + 6 * progress));

    if (!paused) {
      const dt = Math.min(deltaTime, 0.05);
      this._time += dt;
      const t = this._time;

      // --- the clocks: a continuous bar count
      let beatEdge = false, phraseEdge = false;
      if (profile.beatPhase != null) {
        if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
        this._lastBeatPhase = profile.beatPhase;
      } else if (t - this._freeBeatAt >= beat) { this._freeBeatAt = t; beatEdge = true; }
      if (beatEdge) this._beatCount++;
      if (profile.barPhase != null) {
        if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) this._barCount++;
        this._lastBarPhase = profile.barPhase;
        this._bars = this._barCount + profile.barPhase;
      } else {
        this._bars = (this._beatCount + (profile.beatPhase != null ? profile.beatPhase : Math.min(1, (t - this._freeBeatAt) / beat))) / 4;
      }
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
      this._lastPhrase = phrase;
      const majorPhrase = phraseEdge && (phrase % 2 === 0);

      // --- who contracts: each jelly on its own phase of the four bars
      const active = this._jellies.filter((j) => j.active);
      for (let i = 0; i < this._jellies.length; i++) {
        const j = this._jellies[i];
        const on = i < wanted;
        if (on && !j.active) { j.active = true; this._spawn(j, false); }
        if (!on && j.active && j.gain <= 0.01) j.active = false;
        j.mesh.visible = j.active;
        if (!j.active) continue;
        const cycle = Math.floor(this._bars / CONTRACT_EVERY_BARS - j.phase);
        if (j.lastCycle == null) j.lastCycle = cycle;
        if (cycle !== j.lastCycle) { j.lastCycle = cycle; if (t - j.lastSnap > CONTRACT_S + 0.5) this._snap(j, t); }
        j.gain += ((on ? 1 : 0) - j.gain) * Math.min(1, dt * 0.8);
      }
      if (majorPhrase) {
        // Three together: the ones longest since their last snap.
        const sorted = active.slice().sort((a, b) => a.lastSnap - b.lastSnap);
        for (let k = 0; k < Math.min(3, sorted.length); k++) this._snap(sorted[k], t);
      }
      if (this._debugPulse && t >= this._debugPulseAt) { this._debugPulseAt = t + 4; for (const j of active) this._snap(j, t); }

      // --- the animals
      for (const j of this._jellies) {
        if (!j.active) continue;
        const since = t - j.lastSnap;
        // The contraction curve: a fast rise over CONTRACT_S, a slow fall
        // over RELAX_S. The rim reads the same curve RIM_DELAY_S later.
        const curve = (s) => {
          if (s < 0) return 0;
          if (s < CONTRACT_S) { const u = s / CONTRACT_S; return u * u * (3 - 2 * u); }
          const u = Math.min(1, (s - CONTRACT_S) / RELAX_S);
          return 1 - u * u * (2 - u) * 0.999;
        };
        j.c = curve(since);
        j.cRim = curve(since - RIM_DELAY_S);
        // Propulsion: the impulse is delivered during the snap, along the heading.
        if (since >= 0 && since < CONTRACT_S) j.vel.addScaledVector(j.heading, IMPULSE * j.scale * dt / CONTRACT_S);
        // Drift: the heading turns with the curl of a slow field, the lows
        // set how much of it the water carries.
        curl(j.pos.x * 0.5 + j.seed * 20, j.pos.y * 0.5, t * 0.3, _v2);
        j.heading.x += _v2.x * dt * 0.25; j.heading.z += (Math.sin(t * 0.1 + j.seed * 9) * 0.1) * dt;
        j.heading.y += (1.0 - j.heading.y) * dt * 0.15;
        j.heading.normalize();
        const drift = 0.15 + 0.35 * low;
        j.vel.multiplyScalar(Math.exp(-DRAG * dt));
        j.pos.addScaledVector(j.vel, dt);
        j.pos.x += _v2.x * drift * dt * 0.6;
        j.pos.y += (RISE + _v2.y * drift * 0.3) * dt;
        j.spin += j.spinRate * dt;
        // Out by the top: back in below.
        if (!this._single && j.pos.y > this._halfH(j.pos.z) + 3.5) this._spawn(j, false);
        // Close-up: the one animal comes back in from below when it leaves.
        if (this._single && j.pos.y > this._halfH(j.pos.z) + 3.0) { j.pos.y = -this._halfH(j.pos.z) - 3.0; for (const tt of j.tentacles) this._restTentacle(j, tt); }

        // The bell's transform: the apex along the heading, a slow spin.
        _q.setFromUnitVectors(UP, j.heading);
        _m.makeRotationY(j.spin);
        j.mesh.matrix.compose(j.pos, _q, _v.set(j.scale, j.scale, j.scale)).multiply(_m);
        j.mesh.matrixWorld.copy(j.mesh.matrix);
        j.mesh.matrixWorldNeedsUpdate = false;
        const u = j.uniforms;
        u.uC.value = j.c; u.uCRim.value = j.cRim; u.uWave.value = 0.3 + 0.9 * mid; u.uTime.value = t; u.uSpin.value = j.spin;
        u.uPulse.value = since >= 0 ? Math.exp(-since * 2.2) : 0;
        u.uGain.value = j.gain;

        // The tentacles: each node follows the one above with a lag that
        // grows towards the tip, and hangs away from the heading.
        const R = j.scale * BELL_R * (1 - CONTRACT_DEPTH * j.cRim);
        for (const tt of j.tentacles) {
          const ang = tt.az * Math.PI * 2 + j.spin;
          // The attachment on the rim, in the bell's frame.
          _v.set(Math.cos(ang) * tt.r * R, -0.02 * j.scale, Math.sin(ang) * tt.r * R).applyQuaternion(_q).add(j.pos);
          tt.nodes[0].copy(_v);
          const segLen = tt.len * j.scale * BELL_R / TENTACLE_NODES;
          for (let i = 1; i <= TENTACLE_NODES; i++) {
            const prev = tt.nodes[i - 1], node = tt.nodes[i];
            const f = i / TENTACLE_NODES;
            // Rest direction: away from the heading, pulled down; a slow
            // sway of its own so the fringe of arms is never straight.
            _v2.copy(j.heading).multiplyScalar(-1);
            _v2.y -= 0.9;
            _v2.x += Math.sin(t * 0.8 + tt.seed * 6.28 + f * 4.0) * 0.18 * (tt.arm ? 1.6 : 1.0);
            _v2.normalize();
            // The direction the node currently sits at, relaxed towards rest.
            _v.subVectors(node, prev);
            const len = _v.length() || 1e-4;
            _v.multiplyScalar(1 / len);
            _v.lerp(_v2, dt * (0.9 + 1.5 * f)).normalize();
            _v.multiplyScalar(segLen).add(prev);
            // The lag: the top follows fast, the tip slowly.
            const k = 1 - Math.exp(-dt * (16.0 - 12.0 * f));
            node.lerp(_v, k);
          }
        }
      }
    }

    // --- the buffers
    this._writeTentacles(high);
    if (this._particleUniforms) {
      const arr = this._particleUniforms.uJelly.value;
      let n = 0;
      for (const j of this._jellies) { if (!j.active) continue; arr[n].set(j.pos.x, j.pos.y, j.pos.z, j.gain * (0.35 + 0.8 * j.uniforms.uPulse.value) * j.scale); n++; }
      this._particleUniforms.uCount.value = n;
      this._particleUniforms.uTime.value = this._time;
    }
  }

  // Every tentacle as fifteen... sixteen segments: colour = the bioluminescence
  // x the pulse running down the chain x a fade towards the tip (which reads
  // as the line thinning) x distance x safe zone.
  _writeTentacles(high) {
    const t = this._time;
    const camera = this._camera;
    const c = safeZoneUniforms.uSafeCenter.value, hs = safeZoneUniforms.uSafeHalfSize.value, a = safeZoneUniforms.uAspect.value, feather = safeZoneUniforms.uSafeFeather.value;
    const safeAt = (p) => {
      _v.copy(p).project(camera);
      const qx = Math.abs(_v.x * a - c.x) - hs.x, qy = Math.abs(_v.y - c.y) - hs.y;
      const mx = Math.max(qx, 0), my = Math.max(qy, 0);
      const d = Math.sqrt(mx * mx + my * my) + Math.min(Math.max(qx, qy), 0);
      const u = THREE.MathUtils.clamp(d / Math.max(feather, 1e-4), 0, 1);
      return SAFE_FLOOR + (1 - SAFE_FLOOR) * u * u * (3 - 2 * u);
    };
    const pulseGain = 0.5 + 1.0 * high;
    const write = (set, arm) => {
      const pos = set.pos, col = set.col;
      let w = 0;
      for (const j of this._jellies) {
        if (!j.active) continue;
        const since = t - j.lastSnap;
        const dist = Math.max(0.1, -j.pos.z);
        // Kelp's attenuation, once per jelly: paler and bluer with distance.
        const keepR = Math.exp(-dist * VIEW_ATTEN * 0.9), keepG = Math.exp(-dist * VIEW_ATTEN * 0.55), keepB = Math.exp(-dist * VIEW_ATTEN * 0.30);
        const wr = this._water.r * (1 - keepB) * 0.6, wg = this._water.g * (1 - keepB) * 0.6, wb = this._water.b * (1 - keepB) * 0.6;
        for (const tt of j.tentacles) {
          if (tt.arm !== arm) continue;
          for (let i = 0; i < TENTACLE_NODES; i++) {
            const p0 = tt.nodes[i], p1 = tt.nodes[i + 1];
            const f0 = i / TENTACLE_NODES, f1 = (i + 1) / TENTACLE_NODES;
            // The pulse: a wave leaving the rim at the snap, at the tip after PULSE_TRAVEL_S.
            const pulse = (f) => { const d = since - f * PULSE_TRAVEL_S; return since >= 0 ? Math.exp(-d * d * 14.0) * pulseGain : 0; };
            const base = arm ? 0.30 : 0.16;
            const b0 = (base + pulse(f0)) * (1 - 0.75 * f0) * j.gain, b1 = (base + pulse(f1)) * (1 - 0.75 * f1) * j.gain;
            const s0 = safeAt(p0) * b0, s1 = safeAt(p1) * b1;
            const p = w * 6;
            pos[p] = p0.x; pos[p + 1] = p0.y; pos[p + 2] = p0.z; pos[p + 3] = p1.x; pos[p + 4] = p1.y; pos[p + 5] = p1.z;
            col[p] = (BIOLUM[0] * keepR + wr) * s0; col[p + 1] = (BIOLUM[1] * keepG + wg) * s0; col[p + 2] = (BIOLUM[2] * keepB + wb) * s0;
            col[p + 3] = (BIOLUM[0] * keepR + wr) * s1; col[p + 4] = (BIOLUM[1] * keepG + wg) * s1; col[p + 5] = (BIOLUM[2] * keepB + wb) * s1;
            w++;
          }
        }
      }
      set.geo.instanceCount = w;
      set.posBuf.needsUpdate = true;
      set.colBuf.needsUpdate = true;
    };
    write(this._fine, false);
    write(this._arms, true);
  }

  dispose() {
    for (const o of this._objects) if (o.parent) o.parent.remove(o);
    for (const g of this._geometries) g.dispose();
    for (const m of this._materials) m.dispose();
    this._objects = []; this._geometries = []; this._materials = [];
    this._scene = null;
  }
}
